import { Button, createTheme, MantineProvider } from "@mantine/core";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { Popups } from "@/components/@custom/Popups";
import { Toolbar } from "@/components/@custom/Toolbar";
import { WhatsappWidget } from "@/components/@custom/WhatsappWidget";
import { useLogoutOnCompanyMismatch } from "@/features/app/hooks";
import { useSession } from "@/hooks/@custom/useSession";
import { Layout } from "@/pages/_layouts";
import { THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED } from "@/pages/App/Expedition/ExpeditionHome/expedTypes";
import { Role } from "@/store";
import { useGetIpQuery } from "@/store/apis/ipify";
import { useGetQueueDriverPositionQuery, useGetSettingsByCompanyQuery, useSaveCheckinMutation } from "@/store/apis/nextcargo";
import { requireRoles } from "@/utils/requiredRoles";

const theme = createTheme({
  primaryColor: "primary",
  components: {
    Button: Button.extend({
      defaultProps: {
        size: "lg"
      }
    })
  }
});

export const Route = createFileRoute("/app")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Motorista], fallback: "/app/login" }),
  head: () => ({
    meta: [{ title: "Início" }]
  })
});

function RouteComponent() {
  useLogoutOnCompanyMismatch();

  const { driver } = useSession();
  const driverId = driver?.id;
  const driverCompanyId = driver?.company.id;

  const [trigger] = useSaveCheckinMutation();
  const { data: allowedIps } = useGetSettingsByCompanyQuery(
    { id: driverCompanyId! },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const data = state.data?.checkinIp.split(",") || [];
        return {
          ...state,
          data: data
        };
      },
      pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
      refetchOnMountOrArgChange: false
    }
  );
  const { data: ip } = useGetIpQuery(undefined, {
    selectFromResult: (state) => {
      return {
        ...state,
        data: state.data?.ip
      };
    },
    pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
    refetchOnMountOrArgChange: false
  });
  const { data: driverQueue } = useGetQueueDriverPositionQuery(
    { id: driverId! },
    {
      skip: !driverId,
      pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
      refetchOnMountOrArgChange: false
    }
  );

  const queueId = driverQueue?.queue.id;

  const lastAutoCheckinKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!driverId || !driverCompanyId || !queueId || !ip) return;

    const isAllowed = allowedIps.includes(ip);
    const key = driverId + ":" + driverCompanyId + ":" + queueId + ":" + ip;

    if (!isAllowed && lastAutoCheckinKeyRef.current !== key) {
      lastAutoCheckinKeyRef.current = key;
      trigger({
        id: driverId,
        status: false,
        isPresent: false,
        company: { id: driverCompanyId },
        queue: { id: queueId }
      });
    }

    if (isAllowed) {
      lastAutoCheckinKeyRef.current = null;
    }
  }, [driverId, driverCompanyId, queueId, ip]);

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Toolbar.Default />

      <Popups />
      <WhatsappWidget />
      <Layout.Base>
        <Outlet />
      </Layout.Base>
    </MantineProvider>
  );
}
